import type { CogoChatRequest } from '@cogo/chat-ui'
import type { ChatService } from './chat-service'
import type { ChatContext, StreamChunk, CliAction } from '@/store/chat-types'
import { withCogoDevProxyHeaderRecord } from '@/services/cogo-platform-config'
import {
  CHAT_GATEWAY_UNCONFIGURED_CODE,
  CHAT_GATEWAY_UNCONFIGURED_SENTENCE,
} from './chat-gateway-config'
import type { StudioProjectReadinessChatContext } from './project-readiness-chat-context'

export interface GatewayConfig {
  /**
   * The gateway base, or `null` when this build has no chat gateway at all.
   *
   * `null` is an ANSWER, not a missing value: `resolveChatGatewayBase` returns
   * it for a deployment that declares no gateway (and for an appliance build
   * that names none), and `sendMessage` refuses before sending rather than
   * dialling a host that cannot serve `/api/chat-gateway`.
   */
  getGatewayUrl: () => string | null
  getJwt: () => string | undefined
  getProjectId: () => string | null
  getAnonKey: () => string
}

// ─── SSE event types from chat-stream ────────────────────────
interface SSEMeta {
  connected: boolean
  trace_id: string
  project_id: string
}

interface SSEProgress {
  step: string
  progress: number
  details?: Record<string, unknown>
}

interface SSEAction {
  type: string
  [key: string]: unknown
}

interface SSEComplete {
  result: {
    ok: boolean
    trace_id: string
    reply?: string
    actions?: SSEAction[]
    error?: string
  }
}

export class GatewayChatService implements ChatService {
  constructor(private config: GatewayConfig) {}

  async *sendMessage(
    messages: Array<{ role: string; content: string }>,
    context: ChatContext,
  ): AsyncGenerator<StreamChunk> {
    const gatewayUrl = this.config.getGatewayUrl()

    // A transport this build never had is not a transport that failed. Said
    // BEFORE anything is sent, with zero network egress — the same treatment
    // `src/lib/supabase.ts` gives the other unconfigured backend, and the rule
    // `isClosedNetworkDeployment` states for a control with nothing behind it:
    // it must say so before it is pressed.
    if (gatewayUrl === null) {
      yield {
        type: 'error',
        code: CHAT_GATEWAY_UNCONFIGURED_CODE,
        message: CHAT_GATEWAY_UNCONFIGURED_SENTENCE,
      }
      return
    }

    const jwt = this.config.getJwt()
    const anonKey = this.config.getAnonKey()
    const projectId = this.config.getProjectId()
    const runId = crypto.randomUUID()
    // This field is intentionally scoped to Studio Chat rather than the
    // cross-product ChatContext store contract. `enrichChatContext` preserves
    // unknown context fields while resolving the selected editing target.
    const projectReadinessContext = (
      context as ChatContext & {
        projectReadinessContext?: StudioProjectReadinessChatContext | null
      }
    ).projectReadinessContext

    // AG-UI protocol body
    const body = {
      threadId: projectId || crypto.randomUUID(),
      runId,
      toolExecutionMode: 'client-delegated' as const,
      messages: messages.map(m => ({ role: m.role, content: m.content })),
      state: {
        phase: context.phase ?? 'idle',
        appName: context.appName,
        narrative: context.narrative,
        // Phase 4 IA-4 — shell/route/project/block context auto-injected
        // by chat-model-adapter. The gateway treats it as opaque metadata
        // to forward to the LLM system prompt.
        pageContextInstructions: context.pageContextInstructions,
        ontologyContext: context.ontologyContext,
        targetJson: context.targetJson,
        cogoAppContext: context.cogoAppContext,
        projectReadinessContext,
        chatModelPolicy: context.chatModelPolicy,
        operationId: context.operationId,
        operationLabel: context.operationLabel,
        operationKind: context.operationKind,
        operationContract: context.operationContract,
        operationContextFrame: context.operationContextFrame,
        webMcpToolName: context.webMcpToolName,
        studioMcpToolName: context.studioMcpToolName,
        webMcpToolStatus: context.webMcpToolStatus,
        chatOperationInstructions: context.chatOperationInstructions,
      },
    }

    let res: Response
    try {
      res = await fetch(`${gatewayUrl}/api/chat-gateway`, {
        method: 'POST',
        headers: withCogoDevProxyHeaderRecord({
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(jwt && { 'Authorization': `Bearer ${jwt}` }),
        }),
        body: JSON.stringify(body),
      })
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err)
      console.error('[chat-gateway] fetch failed:', detail)
      yield { type: 'error', code: 'NETWORK_ERROR', message: `Network error: ${detail}` }
      return
    }

    if (!res.ok) {
      if (res.status === 401) {
        yield { type: 'error', code: 'AUTH_EXPIRED', message: 'Session expired — please sign in again' }
      } else if (res.status === 429) {
        yield { type: 'error', code: 'RATE_LIMIT', message: 'Too many requests — please wait a moment' }
      } else {
        let detail = ''
        try { const body = await res.json(); detail = body?.error || body?.message || '' } catch { /* ignore */ }
        yield { type: 'error', code: 'GATEWAY_ERROR', message: `Gateway error (${res.status})${detail ? ': ' + detail : ''}` }
      }
      return
    }

    if (!res.body) {
      yield { type: 'error', code: 'STREAM_ERROR', message: 'No response body from stream' }
      return
    }

    // Parse AG-UI SSE stream
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    // Collect tool call args across streaming events
    const pendingToolCalls = new Map<string, { name: string; args: string }>()

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6).trim()
          if (!jsonStr) continue

          let event: { type: string; [key: string]: unknown }
          try { event = JSON.parse(jsonStr) } catch { continue }

          switch (event.type) {
            // StreamChunk text content is a DELTA — the adapter accumulates
            // (`fullText += chunk.content`), and MockChatService yields
            // per-char deltas. Yielding the running total here doubles text.
            case 'TEXT_MESSAGE_CONTENT':
              yield { type: 'text' as const, content: (event.delta as string) ?? '' }
              break

            case 'TOOL_CALL_START': {
              const id = typeof event.toolCallId === 'string' ? event.toolCallId.trim() : ''
              const name = typeof event.toolCallName === 'string' ? event.toolCallName.trim() : ''
              if (!id || !name) {
                yield {
                  type: 'error' as const,
                  code: 'AG_UI_PROTOCOL_ERROR',
                  message: 'AG-UI tool calls require non-empty toolCallId and toolCallName values.',
                }
                break
              }
              pendingToolCalls.set(id, { name, args: '' })
              break
            }
            case 'TOOL_CALL_ARGS': {
              const id = typeof event.toolCallId === 'string' ? event.toolCallId.trim() : ''
              const pending = pendingToolCalls.get(id)
              if (pending) pending.args += (event.delta as string) ?? ''
              break
            }
            case 'TOOL_CALL_END': {
              const id = typeof event.toolCallId === 'string' ? event.toolCallId.trim() : ''
              const pending = pendingToolCalls.get(id)
              if (!id || !pending) {
                yield {
                  type: 'error' as const,
                  code: 'AG_UI_PROTOCOL_ERROR',
                  message: 'AG-UI tool completion did not match one active tool call.',
                }
                break
              }
              const toolName = pending.name
              // cogo_generate_app is dispatched to client MCP for SSE progressive rendering
              // (server Workflow is async with no streaming — client SSE gives real-time updates)
              const actionType = AGUI_TOOL_MAP[toolName]
              let payload: Record<string, unknown> = {}
              try { payload = JSON.parse(pending?.args || '{}') } catch { /* use empty */ }
              if (actionType) {
                yield { type: 'actions' as const, actions: [{ type: actionType, payload, orchestration: { runId, toolCallId: id } }] }
              } else if (isFullyQualifiedCogoToolName(toolName)) {
                yield { type: 'actions' as const, actions: [{ type: toolName, payload, orchestration: { runId, toolCallId: id } }] }
              }
              pendingToolCalls.delete(id)
              break
            }

            case 'TOOL_CALL_RESULT': {
              // Server already executed the tool — check for generation status
              const resultData = event.result as string | undefined
              try {
                const parsed = JSON.parse(resultData ?? '{}')
                if (parsed.status === 'generating' && parsed.workflowId) {
                  // Notify user that generation is in progress (delta append)
                  yield {
                    type: 'text' as const,
                    content: `\n\nGeneration started (${parsed.estimatedDuration ?? '~2 min'}). The result will appear here when ready.`,
                  }
                }
              } catch { /* ignore parse error */ }
              break
            }

            case 'RUN_ERROR':
              yield { type: 'error' as const, code: 'STREAM_ERROR', message: (event.message as string) ?? 'LLM error' }
              break

            // RUN_STARTED, RUN_FINISHED, TEXT_MESSAGE_START/END — ignore
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
  }
}

// ─── AG-UI tool name → CliAction type mapper ─────────────────
export const AGUI_TOOL_MAP: Record<string, string> = {
  cogo_generate_app: 'requestAppGeneration',
  cogo_patch_app: 'applyAppPatch',
  cogo_add_component: 'addElement',
  cogo_remove_component: 'removeElement',
  cogo_add_datasource: 'addDatasource',
  cogo_add_actionflow: 'addFlow',
  cogo_preview: 'setMode',
  cogo_deploy: 'publishProject',
  cogo_validate: 'requestValidation',
  cogo_confirm_narrative: 'confirmNarrative',
  cogo_generate_bdd: 'generateBdd',
  cogo_generate_pages: 'generatePages',
  cogo_apply_theme: 'applyTheme',
  cogo_finalize_app: 'finalizeApp',
}

function isFullyQualifiedCogoToolName(toolName: string): boolean {
  return /^cogo\.[a-z0-9_.-]+$/i.test(toolName)
}

// ─── Legacy SSE action → CliAction mapper (unused with AG-UI) ──

const SSE_TYPE_MAP: Record<string, CliAction['type']> = {
  // UI Element
  ui_update: 'updateElement',
  ui_create: 'addElement',
  ui_delete: 'removeElement',
  // Flow
  flow_create: 'addFlow',
  add_node: 'addNode',
  add_connection: 'addConnection',
  // Navigation / Mode
  set_mode: 'setMode',
  navigate: 'navigate',
  // Pipeline
  set_phase: 'setPhase',
  confirm_narrative: 'confirmNarrative',
  generate_bdd: 'generateBdd',
  generate_pages: 'generatePages',
  generate_page_ui: 'generatePageUi',
  apply_theme: 'applyTheme',
  finalize_app: 'finalizeApp',
  // Theme
  set_theme: 'setThemePreset',
  set_font: 'setFont',
  set_radius: 'setRadius',
  reset_theme: 'resetTheme',
  // Project
  create_project: 'createProject',
  rename_project: 'renameProject',
  // Variable / Datasource / Event
  add_variable: 'addVariable',
  remove_variable: 'removeVariable',
  add_datasource: 'addDatasource',
  remove_datasource: 'removeDatasource',
  add_event: 'addEventBinding',
  remove_event: 'removeEventBinding',
  // Data tab navigation
  open_data_tab: 'openDataTab',
  select_data_category: 'selectDataCategory',
  select_entity: 'selectEntity',
  list_entities: 'listEntities',
  // Generation pipeline
  request_block_generation: 'requestBlockGeneration',
  request_block_generation_v2: 'requestBlockGenerationV2',
  request_page_generation: 'requestPageGeneration',
  request_bdd_generation: 'requestBddGeneration',
  request_validation: 'requestValidation',
  request_app_generation: 'requestAppGeneration',
  request_app_validation: 'requestAppValidation',
  modify_block: 'modifyBlock',
  generation_progress: 'generationProgress',
  generation_complete: 'generationComplete',
}

/** snake_case → camelCase field remapping for Gemini action payloads */
const FIELD_REMAP: Record<string, string> = {
  var_type: 'type',
  ds_type: 'type',
  event_key: 'eventKey',
  primary_color: 'primaryColor',
  font_family: 'fontFamily',
  app_name: 'appName',
  node_type: 'nodeType',
  action_type: 'actionType',
  source_id: 'sourceId',
  target_id: 'targetId',
  page_id: 'pageId',
  component_id: 'componentId',
  component_type: 'componentType',
  // Pipeline
  element_count: 'elementCount',
  page_title: 'pageTitle',
}

function mapSSEActions(actions: SSEAction[]): CliAction[] {
  const result: CliAction[] = []
  for (const a of actions) {
    const cliType = SSE_TYPE_MAP[a.type]
    if (cliType) {
      const { type: _, ...payload } = a
      // Remap snake_case → camelCase fields
      for (const [snakeKey, camelKey] of Object.entries(FIELD_REMAP)) {
        if (snakeKey in payload) {
          payload[camelKey] = payload[snakeKey]
          delete payload[snakeKey]
        }
      }
      result.push({ type: cliType, payload })
    }
  }
  return result
}

// ─── SSE parser ──────────────────────────────────────────────

function parseSSEBlock(block: string): { event: string; data: unknown } | null {
  let event = ''
  let dataStr = ''

  for (const line of block.split('\n')) {
    if (line.startsWith('event: ')) {
      event = line.slice(7).trim()
    } else if (line.startsWith('data: ')) {
      dataStr += line.slice(6)
    }
  }

  if (!event || !dataStr) return null

  try {
    return { event, data: JSON.parse(dataStr) }
  } catch {
    return null
  }
}
